import {
  ClientAdProductType,
  CustomerAnalyticsListingFilterType,
  type CustomerAnalyticsSpecialityHousingType,
  type ListingAlias,
  type PortfolioAnalyticsCustomerDto,
  type PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { mapPackageTypeName } from '../../../utils/mapPackageTypeName';
import { ALL_US_STATES_AND_TERRITORIES_MAP } from '@zg-rentals/states-territories';
import { type MainLayoutContextValue } from './MainLayoutContext';
import { TabsEnum } from './MainPageLayout.config';
import type { DisplayingRegionLevel } from '../../../types/DisplayingRegionLevel';

export type USStateKey = keyof typeof ALL_US_STATES_AND_TERRITORIES_MAP;

export const ALL_PROPERTIES = 'All properties';

// Preferred order for package types
const PACKAGE_TYPE_ORDER = [
  ClientAdProductType.BASE_PACKAGE,
  ClientAdProductType.ENHANCED_PACKAGE,
  ClientAdProductType.PREMIUM_PACKAGE,
  ClientAdProductType.PREMIUM_PLUS_PACKAGE,
  ClientAdProductType.SIGNATURE_PACKAGE,
];

export const getAllPropertiesOptions = (
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
  search: string,
  isMapView: boolean,
  visibleRegionIds: Array<{ regionId: number; regionIdType: DisplayingRegionLevel }>,
): Array<{ value: string; label: string }> => {
  let filteredProperties = [...properties];

  if (isMapView) {
    filteredProperties = filteredProperties.filter((property) =>
      visibleRegionIds.some(
        (region) =>
          region.regionId === property.propertyDetails.stateRegionId ||
          region.regionId === property.propertyDetails.metroStatRegionId ||
          region.regionId === property.propertyDetails.cityRegionId ||
          region.regionId === property.propertyDetails.zipRegionId,
      ),
    );
  }

  return [
    { value: '', label: ALL_PROPERTIES },
    ...filteredProperties
      .filter((property) => (property.propertyDetails.listingName || '').toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (a.propertyDetails.listingName || '').localeCompare(b.propertyDetails.listingName || ''))
      .map((property) => ({
        value: property.propertyDetails.listingAlias,
        label: property.propertyDetails.listingName || '',
        disabled: isMapView && property.aggregateStats.isSingleFamily,
      })),
  ];
};

export const getPackagesOptions = (
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
  selectedFilters: MainLayoutContextValue['selectedFilters'],
): Array<{ value: ClientAdProductType | CustomerAnalyticsListingFilterType; label: string }> => {
  const selectedState = Number(selectedFilters.stateRegionId);
  const selectedMetropolitanServiceArea = Number(selectedFilters.metropolitanServiceAreaRegionId);
  const selectedCity = Number(selectedFilters.cityRegionId);
  const selectedZip = Number(selectedFilters.zipCodeRegionId);

  const filtered = properties.filter((property) => {
    if (selectedState && property.propertyDetails.stateRegionId !== selectedState) return false;
    if (
      selectedMetropolitanServiceArea &&
      property.propertyDetails.metroStatRegionId !== selectedMetropolitanServiceArea
    )
      return false;
    if (selectedCity && property.propertyDetails.cityRegionId !== selectedCity) return false;
    if (selectedZip && property.propertyDetails.zipRegionId !== selectedZip) return false;
    return true;
  });

  const options = filtered
    .flatMap((property) =>
      property.breakdownStats.flatMap((stat) => {
        const productTypes = stat.adproductTypes.length > 0 ? stat.adproductTypes : [null];

        return productTypes.map((productType) => ({
          productType,
          filterType: stat.analyticsListingType,
        }));
      }),
    )
    .reduce(
      (
        acc: Array<{ productType: ClientAdProductType | null; filterType: CustomerAnalyticsListingFilterType }>,
        curr,
      ) => {
        if (curr.filterType === CustomerAnalyticsListingFilterType.PAID && !curr.productType) return acc;

        if (acc.some((item) => item.productType === curr.productType && item.filterType === curr.filterType)) {
          return acc;
        }
        return acc.concat(curr);
      },
      [],
    )
    .map(({ productType, filterType }) => ({
      value: productType || filterType,
      label: mapPackageTypeName(productType, filterType),
      productType,
    }));

  // Sort by preferred package type order
  return options.sort((a, b) => {
    const aIndex = a.productType ? PACKAGE_TYPE_ORDER.indexOf(a.productType as ClientAdProductType) : -1;
    const bIndex = b.productType ? PACKAGE_TYPE_ORDER.indexOf(b.productType as ClientAdProductType) : -1;

    // If both are in the preferred order, sort by their index
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only a is in preferred order, it comes first
    if (aIndex !== -1) {
      return -1;
    }

    // If only b is in preferred order, it comes first
    if (bIndex !== -1) {
      return 1;
    }

    // If neither is in preferred order, sort alphabetically by label
    return a.label.localeCompare(b.label);
  });
};

export const getStatesOptions = (
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
): Array<{ value: number; label: string }> => {
  const seen = new Set<number>();
  const stateOptions: Array<{ value: number; label: string }> = [];

  for (const property of properties) {
    const regionId = property.propertyDetails.stateRegionId;
    if (seen.has(regionId)) continue;
    seen.add(regionId);
    const state = property.propertyDetails.state as USStateKey;
    stateOptions.push({
      value: regionId,
      label: ALL_US_STATES_AND_TERRITORIES_MAP[state] ?? state,
    });
  }

  return stateOptions.sort((a, b) => a.label.localeCompare(b.label));
};

export const getMetropolitanServiceAreasOptions = (
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
  selectedFilters: MainLayoutContextValue['selectedFilters'],
): Array<{ value: number; label: string; stateRegionId: number }> => {
  const selectedState = Number(selectedFilters.stateRegionId);
  const seen = new Set<number>();
  const options: Array<{ value: number; label: string; stateRegionId: number }> = [];

  for (const property of properties) {
    if (selectedState && property.propertyDetails.stateRegionId !== selectedState) continue;
    const regionId = property.propertyDetails.metroStatRegionId;
    if (!regionId || !property.propertyDetails.metroArea || seen.has(regionId)) continue;
    seen.add(regionId);
    options.push({
      value: regionId,
      label: property.propertyDetails.metroArea.replace('Metro Area', 'Metro area'),
      stateRegionId: property.propertyDetails.stateRegionId,
    });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
};

export const getCitiesOptions = (
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
  selectedFilters: MainLayoutContextValue['selectedFilters'],
): Array<{ value: number; label: string; metropolitanServiceAreaRegionId: number; stateRegionId: number }> => {
  const selectedMetropolitanServiceArea = Number(selectedFilters.metropolitanServiceAreaRegionId);
  const selectedState = Number(selectedFilters.stateRegionId);
  const seen = new Set<number>();
  const options: Array<{
    value: number;
    label: string;
    metropolitanServiceAreaRegionId: number;
    stateRegionId: number;
  }> = [];

  for (const property of properties) {
    if (selectedState && property.propertyDetails.stateRegionId !== selectedState) continue;
    if (
      selectedMetropolitanServiceArea &&
      property.propertyDetails.metroStatRegionId !== selectedMetropolitanServiceArea
    )
      continue;
    const regionId = property.propertyDetails.cityRegionId;
    if (!regionId || seen.has(regionId)) continue;
    seen.add(regionId);
    options.push({
      value: regionId,
      label: property.propertyDetails.city,
      metropolitanServiceAreaRegionId: property.propertyDetails.metroStatRegionId,
      stateRegionId: property.propertyDetails.stateRegionId,
    });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
};

export const getZIPCodesOptions = (
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
  selectedFilters: MainLayoutContextValue['selectedFilters'],
): Array<{
  value: number;
  label: string;
  cityRegionId: number;
  metropolitanServiceAreaRegionId: number;
  stateRegionId: number;
}> => {
  const selectedCity = Number(selectedFilters.cityRegionId);
  const selectedMetropolitanServiceArea = Number(selectedFilters.metropolitanServiceAreaRegionId);
  const selectedState = Number(selectedFilters.stateRegionId);
  const seen = new Set<number>();
  const options: Array<{
    value: number;
    label: string;
    cityRegionId: number;
    metropolitanServiceAreaRegionId: number;
    stateRegionId: number;
  }> = [];

  for (const property of properties) {
    if (selectedCity && property.propertyDetails.cityRegionId !== selectedCity) continue;
    if (
      selectedMetropolitanServiceArea &&
      property.propertyDetails.metroStatRegionId !== selectedMetropolitanServiceArea
    )
      continue;
    if (selectedState && property.propertyDetails.stateRegionId !== selectedState) continue;
    const regionId = property.propertyDetails.zipRegionId;
    if (!regionId || seen.has(regionId)) continue;
    seen.add(regionId);
    options.push({
      value: regionId,
      label: property.propertyDetails.zip,
      cityRegionId: property.propertyDetails.cityRegionId,
      metropolitanServiceAreaRegionId: property.propertyDetails.metroStatRegionId,
      stateRegionId: property.propertyDetails.stateRegionId,
    });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
};

export const getCustomersOptions = (
  customers: Array<PortfolioAnalyticsCustomerDto>,
): Array<{ value: string; label: string }> => {
  return customers.map((customer) => ({
    value: customer.customerId,
    label: customer.customerName,
  }));
};

const SPECIALITY_HOUSING_TYPE_LABELS: Record<string, string> = {
  AFFORDABLE: 'Affordable',
  ALL: 'All',
  CONVENTIONAL: 'Conventional',
  DISABLED_HOUSING: 'Disabled housing',
  INCOME_RESTRICTED: 'Income restricted',
  MILITARY_HOUSING: 'Military housing',
  SENIOR_LIVING: 'Senior living',
  STUDENT_HOUSING: 'Student housing',
};

export const getSpecialityHousingTypeOptions = (
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
  selectedFilters: MainLayoutContextValue['selectedFilters'],
): Array<{ value: string; label: string }> => {
  const selectedState = Number(selectedFilters.stateRegionId);
  const selectedMetropolitanServiceArea = Number(selectedFilters.metropolitanServiceAreaRegionId);
  const selectedCity = Number(selectedFilters.cityRegionId);
  const selectedZip = Number(selectedFilters.zipCodeRegionId);

  const filtered = properties.filter((property) => {
    if (selectedState && property.propertyDetails.stateRegionId !== selectedState) return false;
    if (
      selectedMetropolitanServiceArea &&
      property.propertyDetails.metroStatRegionId !== selectedMetropolitanServiceArea
    )
      return false;
    if (selectedCity && property.propertyDetails.cityRegionId !== selectedCity) return false;
    if (selectedZip && property.propertyDetails.zipRegionId !== selectedZip) return false;
    if (selectedFilters.package) {
      if (selectedFilters.package === ClientAdProductType.LISTING_SPOTLIGHT) {
        if ((property.aggregateStats.totalDaysSpotlighted ?? 0) === 0) return false;
      } else if (
        !property.aggregateStats.adproductTypes.includes(selectedFilters.package as ClientAdProductType) &&
        property.aggregateStats.analyticsListingType !== (selectedFilters.package as CustomerAnalyticsListingFilterType)
      ) {
        return false;
      }
    }
    return true;
  });

  const uniqueTypes = new Set(
    filtered.map((property) => property.propertyDetails.specialityHousingType).filter(Boolean),
  );

  return Array.from(uniqueTypes)
    .map((type) => ({
      value: type,
      label: SPECIALITY_HOUSING_TYPE_LABELS[type] ?? type,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const applyFilters = (
  filters: MainLayoutContextValue['selectedFilters'],
  properties: Array<PortfolioAnalyticsPropertyResponseDto>,
) => {
  let filteredProperties = [...properties];
  const havePropertySelected = filters.listingAlias !== '';

  if (havePropertySelected) {
    filteredProperties = filteredProperties.filter(
      (property) => property.propertyDetails.listingAlias === filters.listingAlias,
    );
  }
  if (filters.package && !havePropertySelected) {
    if (filters.package === ClientAdProductType.LISTING_SPOTLIGHT) {
      filteredProperties = filteredProperties.filter(
        (property) => (property.aggregateStats.totalDaysSpotlighted ?? 0) > 0,
      );
    } else {
      filteredProperties = filteredProperties
        .filter(
          (property) =>
            property.aggregateStats.adproductTypes.includes(filters.package as ClientAdProductType) ||
            property.aggregateStats.analyticsListingType === (filters.package as CustomerAnalyticsListingFilterType),
        )
        .map((property) => ({
          ...property,
          breakdownStats: property.breakdownStats.filter(
            (stat) =>
              stat.adproductTypes.includes(filters.package as ClientAdProductType) ||
              stat.analyticsListingType === (filters.package as CustomerAnalyticsListingFilterType),
          ),
        }));
    }
  }
  if (filters.stateRegionId && !havePropertySelected) {
    filteredProperties = filteredProperties.filter(
      (property) => property.propertyDetails.stateRegionId === Number(filters.stateRegionId),
    );
  }
  if (filters.metropolitanServiceAreaRegionId && !havePropertySelected) {
    filteredProperties = filteredProperties.filter(
      (property) => property.propertyDetails.metroStatRegionId === Number(filters.metropolitanServiceAreaRegionId),
    );
  }
  if (filters.cityRegionId && !havePropertySelected) {
    filteredProperties = filteredProperties.filter(
      (property) => property.propertyDetails.cityRegionId === Number(filters.cityRegionId),
    );
  }
  if (filters.zipCodeRegionId && !havePropertySelected) {
    filteredProperties = filteredProperties.filter(
      (property) => property.propertyDetails.zipRegionId === Number(filters.zipCodeRegionId),
    );
  }
  if (filters.specialityHousingType && !havePropertySelected) {
    filteredProperties = filteredProperties.filter(
      (property) => property.propertyDetails.specialityHousingType === filters.specialityHousingType,
    );
  }
  return filteredProperties;
};

export const useFiltersCallbacks = (
  selectedFilters: MainLayoutContextValue['selectedFilters'],
  setSelectedFilters: MainLayoutContextValue['setSelectedFilters'],
  setShouldForceBoundsToGeojsonBounds: (shouldForceBoundsToGeojsonBounds: boolean) => void,
) => {
  const onSelectState = (state: number) => {
    setSelectedFilters({
      ...selectedFilters,
      stateRegionId: state,
      metropolitanServiceAreaRegionId: 0,
      cityRegionId: 0,
      zipCodeRegionId: 0,
    });
    setShouldForceBoundsToGeojsonBounds(!!state);
  };

  const onSelectMetropolitanServiceArea = (metropolitanServiceAreaRegionId: number, stateRegionId: number) => {
    setSelectedFilters({ ...selectedFilters, metropolitanServiceAreaRegionId, stateRegionId });
    setShouldForceBoundsToGeojsonBounds(!!metropolitanServiceAreaRegionId);
  };

  const onSelectCity = (cityRegionId: number, metropolitanServiceAreaRegionId: number, stateRegionId: number) => {
    setSelectedFilters({ ...selectedFilters, cityRegionId, metropolitanServiceAreaRegionId, stateRegionId });
    setShouldForceBoundsToGeojsonBounds(!!cityRegionId);
  };

  const onSelectZIPCode = (
    zipCodeRegionId: number,
    cityRegionId: number,
    metropolitanServiceAreaRegionId: number,
    stateRegionId: number,
  ) => {
    setSelectedFilters({
      ...selectedFilters,
      zipCodeRegionId,
      cityRegionId,
      metropolitanServiceAreaRegionId,
      stateRegionId,
    });
    setShouldForceBoundsToGeojsonBounds(!!zipCodeRegionId);
  };

  const onSelectProperty = (
    listingAlias: ListingAlias | '',
    properties: Array<PortfolioAnalyticsPropertyResponseDto>,
  ) => {
    const selectedProperty = properties.find((p) => p.propertyDetails.listingAlias === listingAlias);

    if (selectedProperty) {
      const stateRegionId = Number(selectedProperty.propertyDetails.stateRegionId);
      const metropolitanServiceAreaRegionId = Number(selectedProperty.propertyDetails.metroStatRegionId);
      const cityRegionId = Number(selectedProperty.propertyDetails.cityRegionId);
      const zipCodeRegionId = Number(selectedProperty.propertyDetails.zipRegionId);
      const pkg =
        selectedProperty.aggregateStats.adproductTypes.length > 0 &&
        selectedProperty.aggregateStats.adproductTypes[0] !== null
          ? selectedProperty.aggregateStats.adproductTypes[0]
          : selectedProperty.aggregateStats.analyticsListingType;

      setSelectedFilters({
        ...selectedFilters,
        listingAlias: selectedProperty.propertyDetails.listingAlias,
        stateRegionId,
        metropolitanServiceAreaRegionId,
        cityRegionId,
        zipCodeRegionId,
        package: pkg,
      });
      setShouldForceBoundsToGeojsonBounds(true);
    } else {
      setSelectedFilters({
        ...selectedFilters,
        listingAlias: '',
        stateRegionId: 0,
        metropolitanServiceAreaRegionId: 0,
        cityRegionId: 0,
        zipCodeRegionId: 0,
        package: '',
      });
      setShouldForceBoundsToGeojsonBounds(false);
    }
  };

  const onSelectPackage = (pkg: ClientAdProductType | CustomerAnalyticsListingFilterType | '') => {
    setSelectedFilters({ ...selectedFilters, package: pkg });
  };

  const onSelectCustomer = (customerId: string) => {
    setSelectedFilters({ ...selectedFilters, customerId });
  };

  const onSelectSpecialityHousingType = (specialityHousingType: CustomerAnalyticsSpecialityHousingType | '') => {
    setSelectedFilters({ ...selectedFilters, specialityHousingType });
  };

  return {
    onSelectState,
    onSelectMetropolitanServiceArea,
    onSelectCity,
    onSelectZIPCode,
    onSelectProperty,
    onSelectPackage,
    onSelectCustomer,
    onSelectSpecialityHousingType,
  };
};

export const getActiveTabFromPathname = (pathname: string): TabsEnum => {
  // Extract the last segment from pathname (e.g., "/overview" → "overview")
  const path = pathname.split('/').pop() || '';

  switch (path) {
    case 'overview':
      return TabsEnum.OVERVIEW;
    case 'market-data':
      return TabsEnum.MARKET_DATA;
    case 'renter-data':
      return TabsEnum.RENTER_DATA;
    case 'adspend':
      return TabsEnum.AD_SPEND;
    case 'industry-reports':
      return TabsEnum.MARKET_REPORTS;
    case 'market-comps':
      return TabsEnum.MARKET_COMPS;
    default:
      return TabsEnum.OVERVIEW;
  }
};
